import type { ReceiptData, ReceiptPrintOptions } from '@matix/types';
import { escapeHtml } from './format.js';
import { buildZatcaQrSvg } from './zatcaQr.js';
import { amountInWords, currencyWord } from './amountInWords.js';
import { headerPromotions, splitNamedDiscount } from './promotionLines.js';

/**
 * The A4 tax invoice.
 *
 * This sheet is a faithful re-implementation of the invoice the ERP team's
 * SSRS report produces (`P_S_52_CashSales`) — the document flowfresh and the
 * other KSA tenants already hand to customers. We render it ourselves because
 * the report server round-trip was too slow to print behind a counter; the
 * paper it produces must not change, so the layout below is measured off a
 * rendering of that report rather than designed afresh:
 *
 *   ┌ logo          │ SUPPLIER INFORMATION / معلومات المورد ┐
 *   │ ┌──────────┐  │ ┌───────────────────────────────────┐ │
 *   │ │ Simple   │  │ │ supplier name / VAT / CR / address │ │
 *   │ │ TAX      │  │ ├─ CLIENT INFORMATION / معلومات ────┤ │
 *   │ │ INVOICE  │  │ │ customer name / VAT / CR / address │ │
 *   │ │ no/ref/  │  │ ├───────────────────────────────────┤ │
 *   │ │ memo/date│  │ │ PROJECT                           │ │
 *   │ └──────────┘  │ └───────────────────────────────────┘ │
 *   ├───────────────┴───────────────────────────────────────┤
 *   │ items: subtotal · tax · taxable · price · qty · unit · │
 *   │        description · product no.   (RTL column order)  │
 *   ├────────────────────────────────────────────────────────┤
 *   │ QR        │ amount subject to VAT … TOTAL INVOICE      │
 *   └────────────────────────────────────────────────────────┘
 *
 * Things here that look arbitrary but are measured, not chosen:
 *
 *   - Every block dimension in `A4_CSS` is millimetres off the reference
 *     sheet (its page is 210 mm wide; 1 reference pixel = 0.3777 mm).
 *   - **Field LABELS follow the till's language; nothing else does.** An
 *     English session prints each label bilingually — Arabic line above
 *     English — because that is what the tenant's customers and the tax
 *     authority have been receiving. An ARABIC session prints the Arabic line
 *     alone: that reader does not need the English, and the doubled lines cost
 *     the vertical space these boxes are measured to. Values, item names, the
 *     document title and the footer are untouched by the flag, except that
 *     `arabic` also orders the item description.
 *   - Column order in the items table reads right-to-left (product no. is the
 *     RIGHTMOST column) even though the document direction is LTR.
 *
 * Two things are not cosmetic:
 *
 *   1. **Per-line tax.** The POS prices lines EX-VAT and taxes the header, so
 *      the line columns are derived: the header discount is allocated across
 *      the lines pro rata, VAT is charged on each line's share, and the LAST
 *      line takes the rounding remainder. That is what makes the columns add
 *      up to the invoice totals exactly, which is the first thing an auditor
 *      checks.
 *   2. **The QR.** A ZATCA QR is a base64 TLV of seller name, VAT number,
 *      timestamp, gross total and VAT amount — not a URL. The payload is
 *      built to match the ERP's byte-for-byte; see `zatcaQr.ts`.
 */

type CurrencyArg = string | { code: string; precision?: number };

/** Bilingual field label: Arabic line above, English line below. */
interface Label {
  ar: string;
  en: string;
}

const L = {
  supplierInfo: { ar: 'معلومات المورد', en: 'SUPPLIER INFORMATION' },
  clientInfo: { ar: 'معلومات العميل', en: 'CLIENT INFORMATION' },

  supplier: { ar: 'اسم المورد:', en: 'SUPPLIER' },
  customer: { ar: 'اسم العميل:', en: 'CUSTOMER NAME' },
  vatNumber: { ar: 'الرقم الضريبي:', en: 'VAT NUMBER' },
  crNumber: { ar: 'رقم السجل التجاري:', en: 'CR. NUMBER' },
  address: { ar: 'العنوان:', en: 'ADDRESS' },
  customerAddress: { ar: 'عنوان العميل:', en: 'ADDRESS' },
  project: { ar: 'اسم المشروع:', en: 'PROJECT' },

  simplifiedTitle: { ar: 'فاتورة ضريبه مبسطة', en: 'Simple TAX INVOICE' },
  taxInvoiceTitle: { ar: 'فاتورة ضريبية', en: 'TAX INVOICE' },
  invoiceNo: { ar: 'رقم الفاتورة', en: 'INVOICE NO.' },
  invoiceRef: { ar: 'مرجع الفاتورة', en: 'INVOICE REFERENCE' },
  memo: { ar: 'البيان', en: 'Memo' },
  notes: { ar: 'الملاحظات', en: 'Notes' },
  invoiceDate: { ar: 'تاريخ الفاتورة', en: 'INVOICE DATE' },

  productNo: { ar: 'رقم الصنف', en: 'PRODUCT NO.' },
  description: { ar: 'وصف الخدمة / المنتج', en: 'DESCRIPTION / PROCEDURES' },
  unit: { ar: 'الوحدة', en: 'UNIT' },
  qty: { ar: 'الكميات', en: 'QTY' },
  price: { ar: 'السعر', en: 'PRICE' },
  taxable: { ar: 'المبلغ الخاضع للضريبة', en: 'Taxable Amount' },
  tax: { ar: 'الضريبة', en: 'Tax' },
  subtotalInclVat: { ar: 'الإجمالي مع الضريبه', en: 'Subtotal (Incl. VAT)' },

  amountSubjectToVat: { ar: 'المبلغ الخاضع للضريبة', en: 'AMOUNT SUBJECT TO VAT' },
  delivery: { ar: 'التوصيل', en: 'DELIVERY' },
  afterDelivery: { ar: 'الاجمالى بعد التوصيل', en: 'AMOUNT AFTER DELIVERY' },
  discount: { ar: 'الخصم', en: ' DISCOUNT' },
  afterDiscount: { ar: 'الاجمالى بعد الخصم', en: 'AMOUNT AFTER DISCOUNT' },
  vatAmount: { ar: 'مبلغ الضريبة', en: 'VAT AMOUNT' },
  totalInvoice: {
    ar: 'إجمالي قيمة الفاتورة (شامل قيمة الضريبة المضافة)',
    en: 'TOTAL INVOICE ( INCLUSIVE OF VAT)',
  },

  totalAmountIn: { ar: 'إجمالي المبلغ', en: 'TOTAL AMOUNT IN' },
  piece: 'Piece',
  eGenerated:
    'This Invoice is electronically generated and therefore does not require a signature or an official stamp.',
  printDate: 'print date',
  printedBy: 'printed by',
  reprint: 'REPRINT · إعادة طباعة',
} satisfies Record<string, Label | string>;

function currencyCode(currency: CurrencyArg): string {
  return typeof currency === 'string' ? currency : currency.code;
}

function currencyPrecision(currency: CurrencyArg): number {
  return typeof currency === 'string' ? 2 : (currency.precision ?? 2);
}

/** 2100 → "2,100.00". Latin digits with grouping, as on the reference sheet. */
function num(value: number, precision: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/** Quantities always carry three decimals on this sheet ("1.000"). */
function qtyText(qty: number): string {
  const safe = Number.isFinite(qty) ? qty : 0;
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

/** A rate prints without decimals when it is whole ("15", "15.5"). */
function rateText(rate: number): string {
  const safe = Number.isFinite(rate) ? rate : 0;
  return Number.isInteger(safe) ? String(safe) : String(Number(safe.toFixed(2)));
}

/**
 * Components of the receipt's entry date.
 *
 * `entryDate` is a NAIVE wall-clock string in the tenant's timezone. Parsing
 * the digits out of the string (rather than `new Date(...)`) keeps a tablet
 * whose clock is set to another country from shifting the invoice date — and
 * keeps the QR timestamp identical to the one the ERP writes.
 */
const NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,7}))?)?/;

interface DateParts {
  y: number; mo: number; d: number; h: number; mi: number; s: number; ms: number;
}

function dateParts(value: string): DateParts | null {
  const m = NAIVE_RE.exec((value ?? '').trim());
  if (m) {
    return {
      y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]),
      h: Number(m[4]), mi: Number(m[5]), s: Number(m[6] ?? 0),
      ms: Number((m[7] ?? '0').padEnd(3, '0').slice(0, 3)),
    };
  }
  const ms = Date.parse((value ?? '').trim());
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return {
    y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate(),
    h: d.getHours(), mi: d.getMinutes(), s: d.getSeconds(), ms: d.getMilliseconds(),
  };
}

const pad = (n: number, len = 2): string => String(n).padStart(len, '0');

/** "13-08-2026" — the invoice date, as the reference sheet prints it. */
function invoiceDateText(value: string): string {
  const p = dateParts(value);
  if (!p) return (value ?? '').trim();
  return `${pad(p.d)}-${pad(p.mo)}-${p.y}`;
}

/** "13/08/2026 01:52" — the "print date" in the page footer. */
function printedAtText(now: Date): string {
  return (
    `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ` +
    `${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

/**
 * The QR's timestamp, in the ERP's exact format: `2026-08-13T01:50:32.847` —
 * local wall clock, milliseconds, and NO zone designator. Decoded from a
 * report-server invoice; a Z here would produce a different payload from the
 * same sale.
 */
export function qrTimestamp(value: string): string {
  const p = dateParts(value) ?? {
    y: 1970, mo: 1, d: 1, h: 0, mi: 0, s: 0, ms: 0,
  };
  return (
    `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}.${pad(p.ms, 3)}`
  );
}

/** One row of the items table, with its share of the discount and its VAT. */
interface PricedLine {
  productNo: string;
  description: string;
  qty: number;
  unitPrice: number;
  net: number;
  taxRate: number;
  tax: number;
  gross: number;
  isReturn: boolean;
}

/**
 * Derive the per-line tax breakdown.
 *
 * The POS gives us ex-VAT line totals plus ONE header discount and ONE tax
 * figure. To print a per-line breakdown that still adds up, the discount is
 * allocated pro rata and the final line absorbs the rounding difference on both
 * the net and the VAT columns — so Σ taxable, Σ VAT and Σ subtotal match the
 * boxed totals to the last unit.
 */
function priceLines(
  data: ReceiptData,
  po: ReceiptPrintOptions,
  arabic: boolean,
  precision: number,
): PricedLine[] {
  const lines = data.items;
  if (lines.length === 0) return [];

  const round = (n: number): number => {
    const f = 10 ** precision;
    return Math.round((n + Number.EPSILON) * f) / f;
  };

  const grossOfLines = lines.reduce((s, l) => s + Math.abs(l.total), 0);
  const discount = Math.max(0, data.totals.discount ?? 0);
  const netTotal = Math.max(0, grossOfLines - discount);

  // Rate: prefer what the order actually charged; otherwise derive it, which
  // keeps older payloads (and fixtures) printing a sane column.
  const rate =
    data.taxPercent != null && data.taxPercent > 0
      ? data.taxPercent
      : netTotal > 0
        ? round((data.totals.tax / netTotal) * 100)
        : 0;

  const out: PricedLine[] = [];
  let netRunning = 0;
  let taxRunning = 0;

  lines.forEach((line, index) => {
    const lineGrossEx = Math.abs(line.total);
    const share = grossOfLines > 0 ? lineGrossEx / grossOfLines : 0;
    const isLast = index === lines.length - 1;

    let net = round(lineGrossEx - discount * share);
    if (isLast) net = round(netTotal - netRunning);
    netRunning = round(netRunning + net);

    let tax = round((net * rate) / 100);
    if (isLast) tax = round((data.totals.tax ?? 0) - taxRunning);
    taxRunning = round(taxRunning + tax);

    const en = typeof line.name === 'string' ? line.name.trim() : '';
    const ar = typeof line.nameAr === 'string' ? line.nameAr.trim() : '';
    const primary = arabic ? ar || en : en || ar;
    const other = arabic ? en : ar;

    // One description column carries everything: the name, the other language,
    // the item code, modifiers and the line note — which is where an equipment
    // spec ends up.
    const parts: string[] = [primary || line.name];
    if (other && other !== primary && po.bilingualItemName) parts.push(other);
    if (po.showItemBarcode && line.barcode) parts.push(line.barcode);
    if (po.printCustomOptions && line.mods && line.mods.length > 0) {
      parts.push(line.mods.join(' · '));
    }
    if (po.printCustomOptions && line.note) parts.push(line.note);

    out.push({
      productNo: line.code ? String(line.code) : String(index + 1),
      description: parts.filter(Boolean).join(' — '),
      qty: Math.abs(line.qty),
      unitPrice: Math.abs(line.price),
      net: line.isReturn ? -net : net,
      taxRate: rate,
      tax: line.isReturn ? -tax : tax,
      gross: round(net + tax) * (line.isReturn ? -1 : 1),
      isReturn: Boolean(line.isReturn),
    });
  });

  return out;
}

/*
 * Geometry.
 *
 * Every length below is millimetres measured off the reference sheet, whose
 * page is 210 mm across 556 px (1 px = 0.3777 mm). Font sizes are CSS px,
 * derived from the reference's glyph advances. Don't "tidy" these numbers —
 * they are the design.
 */
const A4_CSS = `
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    background: #fff; color: #000;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11.7px; line-height: 1.2;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  /* The reference sheet is set in Traditional Arabic — a naskh whose glyphs
     are roughly half its em, so every Arabic size below looks outsized next to
     its English line and is not. Devices without it (Android tablets) fall
     through this list; each fallback carries the size-adjust that makes it
     occupy the same width, so the sheet does not reflow on a different
     machine. */
  @font-face { font-family: ArTrad;     src: local("Traditional Arabic"); }
  @font-face { font-family: ArSimp;     src: local("Simplified Arabic");  size-adjust: 77%; }
  @font-face { font-family: ArTypeset;  src: local("Arabic Typesetting"); size-adjust: 111%; }
  @font-face { font-family: ArMajalla;  src: local("Sakkal Majalla");     size-adjust: 84%; }
  @font-face { font-family: ArNaskh;    src: local("Noto Naskh Arabic");  size-adjust: 66%; }
  @font-face { font-family: ArAmiri;    src: local("Amiri");              size-adjust: 76%; }
  @font-face { font-family: ArSegoe;    src: local("Segoe UI");           size-adjust: 57%; }
  .ar {
    font-family: ArTrad, ArSimp, ArTypeset, ArMajalla, ArNaskh, ArAmiri, ArSegoe,
      Tahoma, Arial, sans-serif;
    direction: rtl; unicode-bidi: isolate;
  }
  .num { unicode-bidi: plaintext; white-space: nowrap; }

  /* The page footer is fixed, so it lands on every printed sheet — the body
     has to keep clear of it or a long invoice prints its totals underneath. */
  .sheet { width: 210mm; padding: 3.8mm 3.4mm 14mm 3.8mm; }

  /* ── Masthead: logo + title card on the left, party cards on the right ── */
  .masthead { display: flex; align-items: flex-start; gap: 2.6mm; }
  .col-left  { width: 69.1mm; flex: 0 0 69.1mm; }
  .col-right { width: 131.4mm; flex: 0 0 131.4mm; }

  /* The logo prints at twice the reference's size, by request. Its BOX keeps
     the reference height — the title card below it starts at a measured 41.2 mm
     and the items table hangs off that — so the bigger mark is fitted inside
     the box and centred, never allowed to grow it. A wide logo hits the column
     width first and simply scales less. */
  .logo { height: 37.4mm; display: flex; align-items: center; justify-content: center; }
  .logo img { max-width: 69mm; max-height: 27.8mm; object-fit: contain; }

  /* The title card. Its rows sit on a fixed 17 mm rhythm so the sheet looks
     identical whether or not a reference/memo was captured. */
  .titlecard {
    height: 98.9mm; background: #ebebeb; padding: 7.5mm 3.8mm 0;
  }
  .doc-title-ar { font-size: 33px; font-weight: 700; color: #a30000; text-align: center; line-height: 0.75; }
  .doc-title-en { margin-top: 1.1mm; font-size: 18.6px; font-weight: 700; color: #595959; text-align: center; line-height: 1.15; }
  table.meta { width: 100%; border-collapse: collapse; margin-top: 6.2mm; table-layout: fixed; }
  table.meta td { height: 16.5mm; vertical-align: middle; padding: 0; }
  table.meta td.v { font-size: 14.3px; font-weight: 700; text-align: left; }
  table.meta td.k { text-align: right; width: 33mm; }
  table.meta .lbl-ar { font-size: 17px; color: #404040; white-space: nowrap; line-height: 0.85; }
  table.meta .lbl-en { font-size: 10.5px; font-weight: 700; color: #404040; white-space: nowrap; }

  /* ── Party cards ── */
  .band {
    height: 7.55mm; text-align: center; font-size: 12.9px; font-weight: 700;
    color: #1a1a1a; padding-top: 1.3mm;
  }
  .band .ar { font-size: 17.5px; }
  .band.grey { background: #f0f0f0; margin-bottom: 2.3mm; }
  .band.blue { background: #ddebf7; margin-bottom: 1.9mm; }
  .card { padding: 0 4.2mm 0 4.2mm; }
  .card.grey { background: #f2f2f2; margin-bottom: 1.5mm; }
  .card.blue { background: #ddebf7; }
  .card.parties { height: 50.6mm; padding-bottom: 2mm; }
  .card.parties.blue { margin-bottom: 2.3mm; }
  .card.project { height: 10.2mm; }
  /* A party row is two lines deep: the Arabic value sits on the Arabic label's
     line and the English value on the English one, which is why the values are
     one cell of two divs rather than two cells. */
  table.party { width: 100%; height: 100%; border-collapse: collapse; table-layout: fixed; }
  table.party td { padding: 0; vertical-align: middle; }
  table.party td.v { width: 58.6%; font-weight: 700; }
  table.party .vl-ar { text-align: right; font-size: 20px; line-height: 0.9; min-height: 3.4mm; }
  table.party .vl-en { text-align: left; font-size: 10.3px; line-height: 1.15; min-height: 3.4mm; }
  table.party .vl-num { text-align: right; font-size: 13.3px; line-height: 1.15; min-height: 3.4mm; }
  table.party td.k { width: 41.4%; text-align: right; }
  table.party .lbl-ar { font-size: 15px; color: #404040; line-height: 0.9; white-space: nowrap; }
  table.party .lbl-en { font-size: 9.5px; font-weight: 700; color: #404040; white-space: nowrap; }
  .seller-ar { color: #a30000; }

  /* ── Items ──
     Column order reads right-to-left: product no. is the RIGHTMOST column,
     the running amounts are on the left. */
  table.items {
    width: 100%; border-collapse: collapse; table-layout: fixed;
    margin-top: 5.3mm;
  }
  table.items th, table.items td { border: 1px solid #000; padding: 0 0.6mm; }
  table.items thead th {
    height: 11mm; background: #f2f2f2; vertical-align: top; text-align: center;
    padding-top: 0.7mm; font-weight: 700; color: #333;
  }
  table.items thead .h-ar { font-size: 14.5px; font-weight: 400; line-height: 0.85; }
  table.items thead .h-en { font-size: 10.4px; }
  table.items tbody td {
    height: 6.6mm; text-align: center; vertical-align: middle; font-size: 11.7px;
    color: #1a1a1a;
  }
  table.items tbody td.desc { font-size: 10.4px; }
  table.items tr.ret td { color: #8a1c1c; }
  table.items tfoot td {
    height: 6mm; background: #f2f2f2; text-align: center; vertical-align: middle;
    font-size: 13.9px; font-weight: 700; color: #1f497d;
  }
  table.items tfoot td.ttl-label { font-size: 11.7px; color: #1a1a1a; }
  table.items tr { break-inside: avoid; page-break-inside: avoid; }
  /* The column heads repeat on every page; the total row must NOT — a tfoot
     repeats by default in paged media, which would print the invoice total at
     the foot of each sheet. */
  table.items thead { display: table-header-group; }
  table.items tfoot { display: table-row-group; }

  /* ── Foot: QR beside the totals ladder ── */
  /* The QR, the ladder and the e-invoice note are one thing: a sheet that
     broke between them would leave a customer holding a page of numbers with
     no code, so they move to the next page together. */
  .closing { break-inside: avoid; page-break-inside: avoid; }
  .foot { display: flex; align-items: flex-start; margin-top: 7.2mm; }
  .qr { width: 39.3mm; margin-left: 5.3mm; margin-top: 5.3mm; }
  .qr svg { display: block; width: 39.3mm; height: 39.3mm; }
  .totals { width: 137.9mm; margin-left: auto; }
  table.totals-t { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.totals-t td { height: 6.04mm; border-bottom: 1px solid #000; padding: 0; }
  table.totals-t td.cur { width: 5.7%; text-align: left; font-size: 11.9px; }
  table.totals-t td.v {
    width: 25.5%; text-align: right; font-size: 14.7px; font-weight: 700;
    padding-left: 1.8mm;
  }
  table.totals-t td.k-en { width: 51.2%; text-align: left; padding-left: 25.3mm; font-size: 11.9px; white-space: nowrap; }
  table.totals-t td.k-ar { width: 17.6%; text-align: right; font-size: 16px; white-space: nowrap; padding-right: 0.7mm; }
  table.totals-t tr.grand td { height: 12.1mm; }
  table.totals-t td.k-grand { text-align: right; padding: 0 0.7mm 0 0; }
  table.totals-t td.k-grand .lbl-ar { font-size: 16px; line-height: 0.9; }
  table.totals-t td.k-grand .lbl-en { font-size: 11.9px; }
  table.totals-t td.words {
    height: 6.04mm; text-align: center; font-size: 14.7px; padding-top: 0.6mm;
  }

  .egen { margin-top: 4.2mm; padding-left: 6mm; text-align: center; font-size: 13.5px; font-weight: 700; }

  /* The page footer repeats on every printed sheet. */
  .pagefoot {
    position: fixed; left: 12.8mm; bottom: 3.5mm; width: 184.4mm;
    font-size: 13.3px; font-weight: 700;
  }
  table.pagefoot-t { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.pagefoot-t td { width: 33.33%; padding: 0; }
  table.pagefoot-t td.mid { text-align: center; }
  table.pagefoot-t td.right { padding-left: 4mm; }
  .pagefoot .v { display: inline-block; margin-left: 4.5mm; }
  .pagefoot .rp { color: #8a1c1c; }
`;

/** A section band heading ("SUPPLIER INFORMATION / معلومات المورد"). */
function band(l: Label, arabicOnly: boolean): string {
  return arabicOnly
    ? ar(l.ar)
    : `${escapeHtml(l.en)} / ${ar(l.ar)}`;
}

/** `<span class="ar">…</span>` — an Arabic run, isolated from the LTR flow. */
function ar(text: string): string {
  return `<span class="ar">${escapeHtml(text)}</span>`;
}

/**
 * The label used by every card on the sheet.
 *
 * **Arabic session: the Arabic line ALONE. English session: both lines**,
 * Arabic over English. The sheet used to be unconditionally bilingual — the
 * paper a Gulf customer already receives — but a till running in Arabic
 * prints for a reader who does not need the English, and the doubled lines
 * cost the vertical space the boxes are measured to. The rule is the field
 * LABELS only: values, item names and the document title are unaffected.
 *
 * `arabicOnly` is the working-language flag, so this follows the till's
 * language switch and nothing else.
 */
function label(
  l: Label,
  arabicOnly: boolean,
  arClass = 'lbl-ar',
  enClass = 'lbl-en',
): string {
  const arLine = `<div class="${arClass} ar">${escapeHtml(l.ar)}</div>`;
  if (arabicOnly) return arLine;
  return arLine + `<div class="${enClass}">${escapeHtml(l.en)}</div>`;
}

/**
 * One row of a party card: the value block, then the bilingual label.
 *
 * `value.ar` lands on the label's Arabic line and `value.en` on its English
 * one. `numeric` is for the fields that carry a single number rather than a
 * name (VAT, CR) — those print on the second line, right-aligned under the
 * Arabic column, exactly as the report lays them out.
 */
function partyRow(
  l: Label,
  arabicOnly: boolean,
  value: { ar?: string; en?: string; numeric?: string },
  arClass = '',
): string {
  const arLine =
    value.ar !== undefined
      ? `<div class="vl-ar ar ${arClass}">${escapeHtml(value.ar)}</div>`
      : '';
  const enLine =
    value.numeric !== undefined
      ? `<div class="vl-num num">${escapeHtml(value.numeric)}</div>`
      : `<div class="vl-en">${escapeHtml(value.en ?? '')}</div>`;
  return (
    `<tr><td class="v">${arLine}${enLine}</td>` +
    `<td class="k">${label(l, arabicOnly)}</td></tr>`
  );
}

/**
 * Compose the A4 tax invoice.
 *
 * `arabic` is the till's working language. It decides which language leads
 * the item description AND whether the field labels print Arabic-only (true)
 * or Arabic-over-English (false) — see `label()`.
 */
export function buildA4Html(
  data: ReceiptData,
  currency: CurrencyArg,
  po: ReceiptPrintOptions,
  arabic: boolean,
): string {
  const pc = data.printConfig;
  const precision = currencyPrecision(currency);
  const code = currencyCode(currency);
  const money = (n: number): string => num(n, precision);

  const lines = priceLines(data, po, arabic, precision);

  // The totals ladder. The POS has no delivery charge of its own, so that row
  // prints zero — it is on the reference sheet and tenants read it.
  const subjectToVat = Math.max(0, data.totals.subtotal ?? 0);
  const delivery = 0;
  const afterDelivery = subjectToVat + delivery;
  const discount = Math.max(0, data.totals.discount ?? 0);
  const afterDiscount = afterDelivery - discount;
  const vatTotal = data.totals.tax ?? 0;
  const grossTotal = data.totals.total ?? afterDiscount + vatTotal;
  const vatRate = lines[0]?.taxRate ?? (data.taxPercent ?? 0);

  // ── Seller ──
  const sellerAr = (pc.companyNameAr ?? '').trim();
  const sellerEn = (pc.companyNameEn ?? '').trim();
  const sellerAddress =
    (pc.branchAddressLine ?? pc.companyAddressAr ?? pc.companyAddressEn ?? '').trim();

  // ── Buyer ──
  const customerAr = (data.customer?.nameAr ?? '').trim();
  const customerEn = (data.customer?.name ?? '').trim();
  const customerVat = (data.customer?.taxNumber ?? '').trim();
  const customerAddress = (data.customer?.address ?? '').trim();

  // A buyer with a VAT number is a B2B sale, which is a full tax invoice
  // rather than the simplified one a walk-in gets.
  const title = customerVat ? L.taxInvoiceTitle : L.simplifiedTitle;

  // ── The ZATCA QR ──
  // Payload shape (tag order, timestamp format, 2-decimal amounts) matches the
  // ERP's byte-for-byte — see `qrTimestamp`.
  const qrSvg = buildZatcaQrSvg({
    sellerName: (sellerAr || sellerEn).trim(),
    vatNumber: (pc.taxRegisterNo ?? '').trim(),
    timestamp: qrTimestamp(data.entryDate),
    total: grossTotal.toFixed(2),
    vat: vatTotal.toFixed(2),
    sizeMm: 39.3,
  });

  // ── Invoice reference / memo ──
  // The report prints the receipt's own Memo there; so do we when it has
  // one. Only when the receipt carries no memo do we fall back to the two
  // things a POS sheet would otherwise lose — the table and how it was paid.
  // The reference box is the leg a return refers to.
  const reference = data.exchange
    ? String(data.exchange.saleEntryNo ?? data.exchange.returnEntryNo ?? '')
    : '';
  const storedMemo = (data.referenceNo ?? data.memo ?? '').trim();
  const memo = storedMemo || [
    data.tableLabel ?? '',
    data.payments
      .map((p) => `${(p.typeName || p.typeNameAr) ?? ''} ${money(p.amount)}`.trim())
      .join(' · '),
  ]
    .filter(Boolean)
    .join(' — ');

  // ── Notes ──
  // The till's Notes box. Its own row under the memo, and ONLY when the
  // receipt carries a note: a sheet without one is byte-identical to what
  // this file printed before the box existed.
  const notesText = (data.notes ?? '').trim();
  const notesRow = notesText
    ? `<tr><td class="v">${escapeHtml(notesText)}</td>` +
      `<td class="k">${label(L.notes, arabic)}</td></tr>`
    : '';

  // ── Items ──
  /** One column heading, under the same Arabic-only / bilingual rule. */
  const th = (l: Label, enExtra = ''): string =>
    `<th><div class="h-ar ar">${escapeHtml(l.ar)}</div>` +
    (arabic ? '' : `<div class="h-en">${escapeHtml(l.en)}${enExtra}</div>`) +
    '</th>';
  const itemHead =
    th(L.subtotalInclVat) +
    th(L.tax, `<br>${escapeHtml(vatRate.toFixed(2))}`) +
    th(L.taxable) +
    th(L.price) +
    th(L.qty) +
    th(L.unit) +
    th(L.description) +
    th(L.productNo);

  const itemBody = lines
    .map(
      (l) =>
        `<tr${l.isReturn ? ' class="ret"' : ''}>` +
        `<td class="num">${escapeHtml(money(l.gross))}</td>` +
        `<td class="num">${escapeHtml(money(l.tax))}</td>` +
        `<td class="num">${escapeHtml(money(l.net))}</td>` +
        `<td class="num">${escapeHtml(money(l.unitPrice))}</td>` +
        `<td class="num">${escapeHtml(qtyText(l.qty))}</td>` +
        `<td>${escapeHtml(L.piece)}</td>` +
        `<td class="desc">${escapeHtml(l.description)}</td>` +
        `<td class="num">${escapeHtml(l.productNo)}</td>` +
        `</tr>`,
    )
    .join('\n');

  const itemFoot =
    `<tr>` +
    `<td class="num">${escapeHtml(money(grossTotal))}</td>` +
    `<td class="num">${escapeHtml(money(vatTotal))}</td>` +
    `<td class="num">${escapeHtml(money(afterDiscount))}</td>` +
    `<td class="ttl-label" colspan="5">` +
    `${escapeHtml(L.totalAmountIn.en)} (${escapeHtml(code)}) ${ar(L.totalAmountIn.ar)}` +
    `</td>` +
    `</tr>`;

  // ── Totals ladder ──
  const symbol = escapeHtml(currencySymbol(code));
  const totalRow = (l: Label, value: number, enSuffix = ''): string =>
    `<tr><td class="cur">${symbol}</td>` +
    `<td class="v num">${escapeHtml(money(value))}</td>` +
    (arabic
      ? `<td class="k-ar ar" colspan="2">${escapeHtml(l.ar)}</td></tr>`
      : `<td class="k-en">${escapeHtml(l.en)}${enSuffix}</td>` +
        `<td class="k-ar ar">${escapeHtml(l.ar)}</td></tr>`);

  // Named promotions take the Discount row's place, one row each, and the
  // Discount row keeps only the unnamed rest (see promotionLines.ts). A sheet
  // without promotions prints the ladder it always has, byte for byte.
  const promos = headerPromotions(data).filter((p) => p.amount > 0);
  const discountRows = (() => {
    if (promos.length === 0) return [totalRow(L.discount, discount)];
    const { unnamed } = splitNamedDiscount(data, arabic);
    const rows = unnamed > 0 ? [totalRow(L.discount, unnamed)] : [];
    for (const p of promos) {
      const en = (p.name ?? '').trim();
      const arName = (p.nameAr ?? '').trim();
      // The label cells are bilingual on an English session: the English name
      // in one, the Arabic in the other — never the same words twice.
      rows.push(totalRow({ en: en || arName, ar: arName || (arabic || !en ? en : '') }, p.amount));
    }
    return rows;
  })();

  const totalsRows = [
    totalRow(L.amountSubjectToVat, subjectToVat),
    totalRow(L.delivery, delivery),
    totalRow(L.afterDelivery, afterDelivery),
    ...discountRows,
    totalRow(L.afterDiscount, afterDiscount),
    totalRow(L.vatAmount, vatTotal, ` (٪${escapeHtml(rateText(vatRate))})`),
    `<tr class="grand"><td class="cur">${symbol}</td>` +
      `<td class="v num">${escapeHtml(money(grossTotal))}</td>` +
      `<td class="k-grand" colspan="2">${label(L.totalInvoice, arabic)}</td></tr>`,
    `<tr><td class="words" colspan="4">` +
      `${escapeHtml(amountInWords(grossTotal, currencyWord(code)))}</td></tr>`,
  ].join('\n');

  const now = new Date();
  const numberForTitle = String(data.orderCode ?? data.orderId);

  return `<!DOCTYPE html>
<html lang="en" dir="ltr">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title.en)} #${escapeHtml(numberForTitle)}</title>
<style>${A4_CSS}</style>
</head>
<body>
<div class="sheet">

<div class="masthead">
  <div class="col-left">
    <div class="logo">${pc.receiptLogo ? `<img src="${escapeHtml(pc.receiptLogo)}" alt="">` : ''}</div>
    <div class="titlecard">
      <div class="doc-title-ar ar">${escapeHtml(title.ar)}</div>
      <div class="doc-title-en">${escapeHtml(title.en)}</div>
      <table class="meta">
        <tr><td class="v num">${escapeHtml(numberForTitle)}</td><td class="k">${label(L.invoiceNo, arabic)}</td></tr>
        <tr><td class="v num">${escapeHtml(reference)}</td><td class="k">${label(L.invoiceRef, arabic)}</td></tr>
        <tr><td class="v">${escapeHtml(memo)}</td><td class="k">${label(L.memo, arabic)}</td></tr>
        ${notesRow}
        <tr><td class="v num">${escapeHtml(invoiceDateText(data.entryDate))}</td><td class="k">${label(L.invoiceDate, arabic)}</td></tr>
      </table>
    </div>
  </div>

  <div class="col-right">
    <div class="band grey">${band(L.supplierInfo, arabic)}</div>
    <div class="card grey parties">
      <table class="party">
        ${partyRow(L.supplier, arabic, { ar: sellerAr, en: sellerEn }, 'seller-ar')}
        ${partyRow(L.vatNumber, arabic, { numeric: (pc.taxRegisterNo ?? '').trim() })}
        ${partyRow(L.crNumber, arabic, { numeric: (pc.registerNo ?? '').trim() })}
        ${partyRow(L.address, arabic, { ar: sellerAddress })}
      </table>
    </div>

    <div class="band blue">${band(L.clientInfo, arabic)}</div>
    <div class="card blue parties">
      <table class="party">
        ${partyRow(L.customer, arabic, { ar: customerAr, en: customerEn })}
        ${partyRow(L.vatNumber, arabic, { numeric: customerVat })}
        ${partyRow(L.crNumber, arabic, { numeric: '' })}
        ${partyRow(L.customerAddress, arabic, { ar: customerAddress })}
      </table>
    </div>

    <div class="card blue project">
      <table class="party">
        ${partyRow(L.project, arabic, { en: '' })}
      </table>
    </div>
  </div>
</div>

<table class="items">
  <colgroup>
    <col style="width:12.4%"><col style="width:9.4%"><col style="width:11.7%">
    <col style="width:8.4%"><col style="width:9.8%"><col style="width:7.9%">
    <col style="width:29%"><col style="width:11.4%">
  </colgroup>
  <thead><tr>${itemHead}</tr></thead>
  <tbody>
${itemBody}
  </tbody>
  <tfoot>${itemFoot}</tfoot>
</table>

<div class="closing">
<div class="foot">
  <div class="qr">${qrSvg}</div>
  <div class="totals">
    <table class="totals-t">
${totalsRows}
    </table>
  </div>
</div>

<div class="egen">${escapeHtml(L.eGenerated)}</div>
</div>

<div class="pagefoot">
  <table class="pagefoot-t">
    <tr>
      <td><span class="k">${escapeHtml(L.printDate)}</span><span class="v num">${escapeHtml(printedAtText(now))}</span>${
        data.isReprint ? `<span class="v rp">${escapeHtml(L.reprint)}</span>` : ''
      }</td>
      <td class="mid" id="pg"></td>
      <td class="right"><span class="k">${escapeHtml(L.printedBy)}</span><span class="v">${escapeHtml(data.cashier.name ?? `#${data.cashier.id}`)}</span></td>
    </tr>
  </table>
</div>

</div>
<script>
/* The footer is fixed, so it repeats on every printed page and cannot carry a
   per-page number. Claim "Page 1 Of 1" only when the sheet really is one page;
   a longer invoice gets a blank cell rather than a wrong count. */
(function () {
  var cell = document.getElementById('pg');
  if (!cell) return;
  var A4_PX = 1122.5; /* 297 mm at 96 dpi */
  if (document.documentElement.scrollHeight <= A4_PX) cell.textContent = 'Page 1 Of 1';
})();
</script>
</body>
</html>`;
}

/** The currency mark printed down the left edge of the totals ladder. */
const CURRENCY_MARKS: Record<string, string> = {
  SAR: '﷼', AED: 'د.إ', KWD: 'د.ك', EGP: 'ج.م',
  QAR: 'ر.ق', BHD: 'د.ب', OMR: 'ر.ع', JOD: 'د.أ',
};

function currencySymbol(code: string): string {
  return CURRENCY_MARKS[code] ?? code;
}
